import { useResolveOrgFromUrlParams } from '@src/hooks/useResolveOrgFromUrlParams';
import { useShowcaseParamSync } from '@src/hooks/useShowcaseParamSync';
import { useSidebarState } from '@src/hooks/useSidebarState';
import {
  useDashboard,
  useDatoClient,
  useDatoPerson,
  useFolderStructure,
  useUserIdentity,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import CloseIcon from '@src/svgs/CloseIcon';
import UploadIcon from '@src/svgs/quick-actions/UploadIcon';
import SearchIconOutline from '@src/svgs/SearchIconOutline';
import { analytics } from '@src/utility/analytics';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import ChatGPTService from '../../services/ChatGPTService';
import { PhotoArtifactTab } from '../../types.ts/story';
import {
  fetchSignedUrls,
  getAiDescriptionResponse,
  requestMissingFields,
} from '../../utility/story';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import { OrbLoadingState } from '../common/OrbLoadingState';
import NewStoriesQuerySubscriptionComponent from '../NewStoriesQuerySubscriptionComponent';
import SearchResultsDropdown from './SearchResultsDropdown';
import StoryManagerFolderView from './StoryManagerFolderView';
import StoryPageDropZone from './user-story/StoryPageDropZone';
import { useSearchResultsDropdown } from './useSearchResultsDropdown';
import { useStoryNavigation } from './useStoryNavigation';
import {
  calculateCompletionPercent,
  dropDeletedStoryParams,
  hasUploadFailed,
  isDashboardVisibleStory,
  isStoryCompressedVideoReady,
  isStoryLoading,
  isStoryOriginalVideoReady,
  isStoryTranscriptionReady,
  isVideoUploadStuck,
} from './utils';

import type { DashboardStory } from '../../types.ts/story';
import type { NewStoryUpdate } from '../NewStoriesQuerySubscriptionComponent';
import type { FC } from 'react';

const logger = LogManager.getLogger('feature:Dashboard');

type Props = {
  params: {
    [k: string]: string;
  };
};

const Dashboard: FC<Props> = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const datoClientStore = useDatoClient();
  const datoPerson = useDatoPerson();
  const folderStore = useFolderStructure();
  const { setCollapsed } = useSidebarState();

  // Note: scroll position is saved/restored by StoryManagerFolderView (the
  // document scrolls, not an inner container). On a fresh entry / org switch the
  // view store's scrollTop is reset to 0, so the restore lands at the top.

  // Collapse the app sidebar when entering Story Manager
  useEffect(() => {
    setCollapsed(true);
  }, []);

  const dashboardStore = useDashboard();
  const {
    allStories,
    setAllStories,
    addStory,
    removeStories,
    markStoriesPopulated,
    clearPopulationState,
    isStoryPopulated,
  } = dashboardStore;
  const {
    handleNewStoryProgress,
    getNewStoryCompletionPercent,
    setNewStoryCompletionPercent,
    openNewModal,
    releaseLocalVideo,
  } = useUserStoryUploadFlow();

  // Using datoPerson here will make loading stories/clips faster,
  // because they won't have to wait until initializeData finishes.
  const album = datoPerson.selectedOrganization || videoCreator.organization;
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();

  // When the Dashboard has no album loaded (e.g. person has no orgs in this
  // environment, or a hard refresh), resolve it from the storyId/showcase URL.
  const resolvingOrg = useResolveOrgFromUrlParams();
  // A `?showcase=` deep link from a teammate wins over the workspace this
  // session happened to have selected.
  useShowcaseParamSync();
  const {
    storyManagerEnableAddStoriesButton,
    storyManagerAddStoriesEnableButtons,
    contentStudioDashboardEnableAnalytics,
  } = useFlagsCombination(userIdentity.currentRole);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const gptServiceRef = useRef(
    new ChatGPTService(videoCreator, datoClientStore.aiPromptRepository),
  );
  const gptService = gptServiceRef.current;

  // Add performance tracking reference
  const pageLoadStartTime = useRef(Date.now());
  const loadingCompleted = useRef(false);

  // Track if Zoom OAuth callback has been processed to prevent duplicate handling
  const zoomCallbackProcessed = useRef(false);

  // Handle Zoom OAuth callback
  useEffect(() => {
    const handleZoomCallback = async () => {
      const zoomAuth = urlSearchParams.get('zoom_auth');

      // Skip if already processed or no auth param
      if (!zoomAuth || zoomCallbackProcessed.current) return;

      // Mark as processed IMMEDIATELY to prevent re-runs from dependency changes
      zoomCallbackProcessed.current = true;

      const message = urlSearchParams.get('message');
      const zoomEmail = urlSearchParams.get('email'); // Zoom account email

      if (zoomAuth) {
        if (zoomAuth === 'success') {
          videoCreator.showToast({
            state: 'success',
            message: `Zoom account connected successfully${
              zoomEmail ? `: ${zoomEmail}` : ''
            }`,
          });

          // Do NOT automatically open the Add Stories modal on OAuth callback
          // User should manually click to open it after returning
        } else if (zoomAuth === 'error') {
          videoCreator.showToast({
            state: 'error',
            message: `Failed to connect Zoom account${
              message ? `: ${message.replace(/_/g, ' ')}` : ''
            }`,
          });
          // Do NOT automatically open modal on error - let user manually retry
        }

        // Clean up URL parameters
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('zoom_auth');
        newUrl.searchParams.delete('message');
        newUrl.searchParams.delete('email');
        newUrl.searchParams.delete('user_email');
        newUrl.searchParams.delete('zoom_token_data');
        window.history.replaceState({}, '', newUrl.toString());
      }
    };

    handleZoomCallback();
  }, [urlSearchParams, videoCreator, openNewModal, datoPerson]);

  // Lazy-initialized true when this album's stories aren't already in the store.
  // The load effect below only flips it after the first paint, and that one
  // frame of empty page between the boot overlay and this loader is what made a
  // single refresh look like two separate waits.
  const [storiesLoading, setStoriesLoading] = useState(
    () => !!album?.id && dashboardStore.allStoriesAlbumId !== album.id,
  );
  const populationAbortController = useRef<AbortController | null>(null);

  // Fetch + enrich the album's stories into the store. `silent` skips the
  // spinner and population reset, so a background refresh on return updates the
  // list in place without a loading flash.
  const loadStories = useCallback(
    async (signal: AbortSignal, { silent }: { silent: boolean }) => {
      if (!album?.id) return;

      // Clear population state only for a fresh load, not a silent refresh —
      // clearing would flicker already-populated thumbnails back to loading.
      if (!silent) {
        clearPopulationState();
        setStoriesLoading(true);
      }

      try {
        logger.debug('Load stories start', { album: album.title });

        // Fetch stories from DatoCMS
        const stories = (await videoCreator.findManyStories(
          album.id,
        )) as DashboardStory[];

        if (signal.aborted) return;

        // Identify AWS stories that need signed thumbnail URLs
        const awsStoryIds = stories.filter(s => s.useAws).map(s => s.id);

        // Fetch signed URLs in parallel (fires while we process below)
        const signedUrlsPromise =
          awsStoryIds.length > 0
            ? fetchSignedUrls(awsStoryIds, signal)
            : Promise.resolve({});

        // Mark non-AWS stories as populated immediately
        const alreadyPopulatedIds = stories
          .filter(s => !s.useAws && s.originalVideo)
          .map(s => s.id);
        if (alreadyPopulatedIds.length > 0) {
          markStoriesPopulated(alreadyPopulatedIds);
        }

        // Wait for signed URLs
        const signedUrls = await signedUrlsPromise;

        if (signal.aborted) return;

        // Merge signed thumbnail URLs into stories
        const enrichedStories = stories.map(story => {
          const signed = signedUrls[story.id];
          if (!story.useAws || !signed) return story;

          return {
            ...story,
            selfHostedVideo: {
              url: '',
              id: story.id,
              height: 0,
              width: 0,
              video: {
                duration: signed.duration,
                thumbnailUrl: signed.thumbnailUrl,
                mp4Url: '',
                framerate: 0,
              },
            },
            datoHostedVideo: story.originalVideo,
          } as DashboardStory;
        });

        setAllStories(enrichedStories, album.id);
        setStoriesLoading(false);

        // Deferred: story-level shared contents (platform/analytics columns).
        // Fire-and-forget so the expensive inverse-reference lookups never
        // block the list paint; merged into the store when they arrive.
        void dashboardStore.loadSharedContentsForAllStories(album.id);

        // Mark all stories as populated since thumbnails are now available
        markStoriesPopulated(awsStoryIds);

        // Log performance metrics
        if (!loadingCompleted.current) {
          loadingCompleted.current = true;
          const loadTime = Date.now() - pageLoadStartTime.current;
          logger.info('Load stories complete', {
            album: album.title,
            count: stories.length,
            loadTimeMs: loadTime,
          });
        }
      } catch (error) {
        logger.error('Load stories failed', extractErrorInfo(error));
        setStoriesLoading(false);
      }
    },
    [
      album?.id,
      album?.title,
      videoCreator,
      dashboardStore,
      clearPopulationState,
      markStoriesPopulated,
      setAllStories,
    ],
  );

  const startLoad = useCallback(
    (opts: { silent: boolean }) => {
      // Abort any in-progress request before starting the next.
      populationAbortController.current?.abort();
      populationAbortController.current = new AbortController();
      void loadStories(populationAbortController.current.signal, opts);
    },
    [loadStories],
  );

  // Load on mount / album change. When the store already holds this album (the
  // case on return), render the cached list immediately and refresh silently in
  // the background — no spinner. Otherwise do a full load with the spinner.
  useEffect(() => {
    if (!album?.id) return;
    const cached = dashboardStore.allStoriesAlbumId === album.id;
    // Scope the view state (selection, expansion, scroll, …) to this org. No-op
    // on the same album, so returning to Story Manager preserves it; on an org
    // switch it clears so the previous org's state doesn't leak onto new rows.
    // Runs before startLoad — while the spinner is up and StoryManagerFolderView
    // is unmounted — so scroll restore reads the reset value on remount.
    dashboardStore.storyManagerViewStore.resetForShowcase(album.id);
    startLoad({ silent: cached });
    // The folder view can't mount until the stories land, so fetch its
    // structure alongside them rather than after — a `?folder=` link then
    // resolves as soon as the list does. The view's own call joins this one.
    void folderStore.loadStructure(album.id);
    return () => {
      populationAbortController.current?.abort();
    };
  }, [album?.id]);

  function resetStoryStates(storyId: string) {
    const prevStoryId = urlSearchParams.get('storyId');
    if (prevStoryId !== storyId) {
      videoCreator.selectedPhotoAssets = {
        tab: PhotoArtifactTab.story,
        resource: undefined,
      };
      videoCreator.talkingPointContent = null;
      if (gptService.talkingPointController) {
        gptService.talkingPointController.abort();
      }
    }
  }

  const initNewStoryCompletionPercentRecordItem = useCallback(
    (story: DashboardStory) => {
      if (isStoryLoading(story)) {
        setNewStoryCompletionPercent(
          story.id,
          calculateCompletionPercent(story),
        );
      }
    },
    [],
  );

  const handleSearchQueryChange = useCallback((text: string) => {
    setSearchQuery(text);
  }, []);

  const [localSearch, setLocalSearch] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const onSearchChange = useCallback(
    (value: string) => {
      setLocalSearch(value);
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(
        () => handleSearchQueryChange(value),
        500,
      );
    },
    [handleSearchQueryChange],
  );

  const onSearchClear = useCallback(() => {
    setLocalSearch('');
    clearTimeout(searchTimerRef.current);
    handleSearchQueryChange('');
  }, [handleSearchQueryChange]);

  const handleNewStoryUpdateReceived = async (
    storyUpdates: NewStoryUpdate[],
  ) => {
    const newAllStories: DashboardStory[] = [];
    for (let story of allStories) {
      const storyUpdate = storyUpdates.find(u => u.id === story.id);
      if (storyUpdate) {
        // setting correct original video in update before checking if story should be updated
        // original video isn't coming for AWS, avoid resetting it to undefined if story has it already
        // original video coming as datoHostedVideo for Dato
        storyUpdate.originalVideo = story.useAws
          ? story.originalVideo
          : storyUpdate.datoHostedVideo;
        if (
          (!isStoryOriginalVideoReady(story) &&
            isStoryOriginalVideoReady(storyUpdate)) ||
          (!isStoryCompressedVideoReady(story) &&
            isStoryCompressedVideoReady(storyUpdate))
        ) {
          try {
            const abortController = new AbortController();
            const selfHostedVideo = (
              await requestMissingFields(
                story.id,
                ['originalVideo'],
                abortController.signal,
              )
            ).originalVideo;
            if (story.useAws) {
              storyUpdate.originalVideo = selfHostedVideo!;
            }
          } catch (error) {
            if (error instanceof Error && error.name !== 'AbortError') {
              logger.warn(
                'Failed to fetch self-hosted video for story update',
                extractErrorInfo(error),
              );
            }
          }
        }
        notifyAboutNewStoryUpdate(storyUpdate);
        newAllStories.push({ ...story, ...storyUpdate, isNew: true });
      } else {
        newAllStories.push(story);
      }
    }
    setAllStories(newAllStories);
  };

  const notifyAboutNewStoryUpdate = (storyUpdate: NewStoryUpdate) => {
    if (hasUploadFailed(storyUpdate)) {
      handleNewStoryProgress(100, storyUpdate.id);
      // videoCreator.toastState = {
      //   state: 'error',
      //   message: 'Failed to upload new story',
      // };
      return;
    }

    if (
      isStoryOriginalVideoReady(storyUpdate) &&
      isStoryTranscriptionReady(storyUpdate) &&
      isStoryCompressedVideoReady(storyUpdate)
      // prevent if update is not new
      // !(
      //   isStoryOriginalVideoReady(item) &&
      //   isStoryTranscriptionReady(item) &&
      //   isStoryCompressedVideoReady(item)
      // )
    ) {
      handleNewStoryProgress(100, storyUpdate.id);
      // videoCreator.toastState = {
      //   state: 'success',
      //   message: 'Story successfully uploaded',
      // };
      return;
    }

    if (
      isStoryOriginalVideoReady(storyUpdate)
      // prevent if update is not new
      // !isStoryOriginalVideoReady(item)
    ) {
      handleNewStoryProgress(75, storyUpdate.id);
      return;
    }

    // if last update is over one minute ago, consider it failed
    if (!hasUploadFailed(storyUpdate) && isVideoUploadStuck(storyUpdate)) {
      handleNewStoryProgress(100, storyUpdate.id);
      // save upload failed state
      try {
        const timestamp = new Date();
        datoClientStore.storyRepository!.updateStoryStepsLog({
          id: storyUpdate.uploadLog.id,
          initial_upload: JSON.stringify({
            status: 'fail',
            updatedAt: timestamp,
          }),
          handle_failure: JSON.stringify({
            status: 'fail',
            error: 'Video upload stuck',
            updatedAt: timestamp,
          }),
        });
        logger.info('Video upload stuck, marked as failed', {
          storyId: storyUpdate.id,
        });
      } catch (err) {
        logger.error(
          'Failed to update story upload log for stuck video',
          extractErrorInfo(err),
        );
      } finally {
        logger.warn('Video upload stuck detected', {
          storyId: storyUpdate.id,
        });
      }
      videoCreator.showToast({
        state: 'error',
        message: 'Failed to upload new story',
      });
      return;
    }

    if (
      storyUpdate.useAws &&
      storyUpdate.uploadLog?.combineResults?.status === 'fail'
      // prevent if update is not new
      // !(item.uploadLog?.combineResults?.status === 'fail')
    ) {
      handleNewStoryProgress(100, storyUpdate.id);
      videoCreator.showToast({
        state: 'error',
        message: 'Transcription generation failed for new story',
      });
      return;
    }

    if (
      !storyUpdate.useAws &&
      storyUpdate.externalUploadStatus === 'FAILED'
      // prevent if update is not new
      // !(item.externalUploadStatus === 'FAILED')
    ) {
      handleNewStoryProgress(100, storyUpdate.id);
      videoCreator.showToast({
        state: 'error',
        message: 'Failed to upload new story',
      });
      return;
    }

    if (storyUpdate.useAws && storyUpdate.uploadLog?.initialUpload?.progress) {
      handleNewStoryProgress(
        Math.round(storyUpdate.uploadLog?.initialUpload?.progress / 2),
        storyUpdate.id,
      );
    }
  };

  const handleAddStoriesButtonClick = () => {
    analytics.track('add_stories_button_clicked');

    openNewModal();
  };

  const newStoryIds = allStories
    .filter(s => isStoryLoading(s) || !getAiDescriptionResponse(s))
    .map(s => s.id);

  const getStoryCompletionPercent = useCallback(
    (s: DashboardStory): number => {
      return getNewStoryCompletionPercent(s.id);
    },
    [getNewStoryCompletionPercent],
  );

  const getStoriesWithCompletionPercent = useCallback(
    (stories: DashboardStory[]) => {
      return stories.map(s => {
        return { ...s, completionPercent: getStoryCompletionPercent(s) };
      });
    },
    [getStoryCompletionPercent],
  );

  // Optimistic update and rollback go through the store, not the render's
  // `allStories` snapshot, so overlapping deletes don't undo each other.
  const handleStoriesDelete = async (stories: DashboardStory[]) => {
    const storyIds = stories.map(s => s.id);
    removeStories(storyIds);

    try {
      await videoCreator.softDeleteStories(storyIds);
      // Nothing can play the session's locally-held upload (PLAT-11228) for
      // a deleted story — free it.
      storyIds.forEach(releaseLocalVideo);
      // The open story is gone: unload it and drop it from the URL, or the
      // next access check redirects away (PLAT-11622).
      if (videoCreator.story && storyIds.includes(videoCreator.story.id)) {
        videoCreator.resetStateBeforeInitialising();
      }
      const nextParams =
        album &&
        dropDeletedStoryParams(
          new URLSearchParams(window.location.search),
          storyIds,
          album.slug,
        );
      if (nextParams) {
        setUrlSearchParams(nextParams, { replace: true });
      }
    } catch (e) {
      videoCreator.showToast({
        state: 'error',
        message:
          stories.length === 1
            ? 'Failed to delete story'
            : 'Failed to delete stories',
      });
      stories.forEach(addStory);
    }
  };

  const handleStoryRename = async (
    storyId: DashboardStory['id'],
    newTitle: DashboardStory['title'],
  ) => {
    setAllStories(
      allStories.map(s => (s.id === storyId ? { ...s, title: newTitle } : s)),
    );
    try {
      await videoCreator.updateStory({ id: storyId, title: newTitle });
    } catch (e) {
      videoCreator.showToast({
        state: 'error',
        message: 'Failed to rename story',
      });
      setAllStories(allStories);
    }
    if (videoCreator.story?.id === storyId) {
      videoCreator.story.title = newTitle;
    }
  };

  // The search dropdown must not surface stories the manager itself hides
  // (in-progress/failed uploads for external users, cancelled uploads, …).
  // It derives query matches itself (buildSearchDropdownResults re-groups
  // folders/stories/clips from scratch), so it only needs the full,
  // visibility-filtered list — no query pre-filtering here.
  const isInternalUser = userIdentity.isInternalUser();
  const dropdownStories = useMemo(
    () =>
      allStories.filter(s => isDashboardVisibleStory(s, { isInternalUser })),
    [allStories, isInternalUser],
  );

  const { openStory, openClip } = useStoryNavigation(resetStoryStates);

  const searchDropdown = useSearchResultsDropdown({
    query: searchQuery.trim(),
    structure: folderStore.structure,
    stories: dropdownStories,
    onSelectFolder: folder => {
      // Entering the folder with the query still active would re-filter its
      // contents (often to nothing) — clear the search so the folder shows
      // in full, with the breadcrumb pointing at it.
      folderStore.enterFolder(folder.id);
      onSearchClear();
    },
    onSelectStory: openStory,
    onSelectClip: openClip,
  });

  const renderContent = () => {
    if (!album?.id) return null;
    // The visible list is deliberately NOT filtered by the search query —
    // search results live exclusively in the dropdown. It IS filtered by
    // dashboard visibility (via `dropdownStories`) so the folder view and its
    // tile counts only ever reflect stories the user can actually see.
    const storiesForView = getStoriesWithCompletionPercent(dropdownStories);
    const recentStoryProps = {
      stories: storiesForView,
      resetStoryStates,
      onStoryDelete: (story: DashboardStory) => handleStoriesDelete([story]),
      onStoriesDelete: handleStoriesDelete,
      onStoryRename: handleStoryRename,
      initPercentCompleted: initNewStoryCompletionPercentRecordItem,
      isStoryPopulated,
      enableAnalytics: contentStudioDashboardEnableAnalytics,
    };
    return (
      <StoryManagerFolderView
        showcaseId={album.id}
        showcaseSlug={album.slug}
        recentStoryProps={recentStoryProps}
      />
    );
  };

  return (
    <Main data-testid="story-manager-page">
      <StoryPageDropZone
        enabled={storyManagerEnableAddStoriesButton}
        entryPoint="story-manager-drop"
        showFolder
      />
      <TopContent>
        <TitleGroup>
          <Title>Story Manager</Title>
          <Subtitle>Manage your stories and clips</Subtitle>
        </TitleGroup>
        <DashboardSearchWrapper ref={searchDropdown.triggerRef}>
          <SearchIconOutline strokeColor="var(--text-muted)" />
          <DashboardSearchInput
            type="text"
            placeholder="Search stories and clips"
            value={localSearch}
            onChange={e => {
              searchDropdown.notifyTyping();
              onSearchChange(e.target.value);
            }}
            onKeyDown={searchDropdown.handleInputKeyDown}
          />
          {localSearch && (
            <DashboardClearButton
              aria-label="Clear search"
              onClick={onSearchClear}
            >
              <CloseIcon width={10} height={10} />
            </DashboardClearButton>
          )}
        </DashboardSearchWrapper>
        <SearchResultsDropdown
          isOpen={searchDropdown.isOpen}
          menuRef={searchDropdown.menuRef}
          style={searchDropdown.style}
          query={searchQuery.trim()}
          results={searchDropdown.results}
          activeIndex={searchDropdown.activeIndex}
          onSelect={searchDropdown.selectResult}
        />
        {storyManagerEnableAddStoriesButton &&
          storyManagerAddStoriesEnableButtons && (
            <>
              <AddStoriesButton
                onClick={handleAddStoriesButtonClick}
                data-testid="open-add-stories"
              >
                {/* PLAT-11877 — the Home page's Add Story glyph: one glyph
                    for adding a story, wherever the button is. */}
                <UploadIcon width="16" height="16" />
                Add Stories
              </AddStoriesButton>
            </>
          )}
      </TopContent>

      {/* The same orb and the same copy as the boot gate in EditorEntry, on
          purpose: a refresh hands off from that gate to this one, and a second
          indicator — a ring, or the orb with different words — reads as a second
          wait rather than the rest of the first.

          Regional, not the full-screen overlay: this page is also reached by
          clicking Story Manager in the nav on an already-loaded app, and a fixed
          overlay blacked out the nav and this page's own header for a wait that
          is only the story list. The orb takes the place of the list it is
          fetching, so the page arrives first and fills in. */}
      {storiesLoading || resolvingOrg ? (
        <OrbLoadingState text="Loading…" />
      ) : (
        <ContentWrapper>{renderContent()}</ContentWrapper>
      )}

      {!!newStoryIds.length && (
        <NewStoriesQuerySubscriptionComponent
          key={newStoryIds.join('_')}
          storyIds={newStoryIds}
          onDataReceived={handleNewStoryUpdateReceived}
          onError={() => {
            videoCreator.showToast({
              state: 'error',
              message: 'Failed to update new story',
            });
          }}
        />
      )}
    </Main>
  );
});

export default Dashboard;

const Main = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: var(--spacing-xl) var(--spacing-2xl) var(--spacing-md);
`;

const TopContent = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  min-width: 0;
`;
const Title = styled.h2`
  margin: 0;
  font-size: var(--text-xl);
  color: var(--foreground);
  font-weight: var(--font-weight-semi-bold);
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  color: var(--text-secondary);
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  margin-right: auto;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
`;

/* ─── Search Bar ─── */

const DashboardSearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 400px;
  padding: 8px 12px;
  background-color: var(--input-background);
  border: 1px solid var(--input-border);
  border-radius: var(--radius-input);
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--text-muted);
  }

  &:focus-within {
    border-color: var(--accent);
  }
`;

const DashboardSearchInput = styled.input`
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);

  &::placeholder {
    color: var(--text-muted);
  }
`;

const DashboardClearButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: var(--text-muted);
  display: flex;
  align-items: center;

  &:hover {
    color: var(--text-secondary);
  }
`;

/* ─── Add Stories Button ─── */

const AddStoriesButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  height: 40px;
  width: auto;
  min-width: 160px;
  flex-shrink: 0;
  padding: 0 16px;
  border-radius: 8px;
  background: var(--primary);
  color: var(--background);
  border: 1px solid var(--primary);
  font-size: 14px;
  font-weight: 700;
  line-height: normal;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }
`;
